import { useState, useEffect, useMemo, useCallback } from 'react'

interface DeviceInfo {
  isMobile: boolean
  isTablet: boolean
  isDesktop: boolean
  screenWidth: number
  screenHeight: number
  deviceType: 'mobile' | 'tablet' | 'desktop'
}

// 防抖函数 - 减少频繁更新
function debounce<T extends (...args: any[]) => any>(func: T, wait: number): T {
  let timeout: NodeJS.Timeout
  return ((...args: any[]) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }) as T
}

// 设备类型检测 - 优化版本
function getDeviceType(width: number): 'mobile' | 'tablet' | 'desktop' {
  if (width <= 640) return 'mobile'
  if (width <= 1024) return 'tablet'
  return 'desktop'
}

// 初始设备信息 - 基于服务器端渲染
const getInitialDeviceInfo = (): DeviceInfo => ({
  // Keep SSR and the first client render identical; measure after hydration.
  isMobile: false,
  isTablet: false,
  isDesktop: true,
  screenWidth: 1024,
  screenHeight: 768,
  deviceType: 'desktop'
})

export function useMobile(): DeviceInfo {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>(getInitialDeviceInfo)

  // 优化的设备信息更新函数
  const updateDeviceInfo = useCallback(() => {
    const width = window.innerWidth
    const height = window.innerHeight
    const deviceType = getDeviceType(width)

    setDeviceInfo(prev => {
      // 只在设备类型真正改变时更新
      if (prev.deviceType === deviceType && 
          prev.screenWidth === width && 
          prev.screenHeight === height) {
        return prev
      }

      return {
        isMobile: deviceType === 'mobile',
        isTablet: deviceType === 'tablet',
        isDesktop: deviceType === 'desktop',
        screenWidth: width,
        screenHeight: height,
        deviceType
      }
    })
  }, [])

  // 防抖版本的更新函数 - 移动端更激进的防抖
  const debouncedUpdateDeviceInfo = useMemo(
    () => debounce(updateDeviceInfo, 300), // 从200ms增加到300ms
    [updateDeviceInfo]
  )

  useEffect(() => {
    // 只在客户端执行
    if (typeof window === 'undefined') return

    // 初始化
    updateDeviceInfo()

    // 使用 passive 监听器提高性能
    const resizeHandler = () => {
      // 移动端使用更激进的优化
      const isMobile = window.innerWidth <= 768;
      
      if (typeof requestIdleCallback !== 'undefined') {
        requestIdleCallback(debouncedUpdateDeviceInfo, { 
          timeout: isMobile ? 3000 : 1000 // 移动端更长的超时时间
        })
      } else {
        // 降级到 requestAnimationFrame
        requestAnimationFrame(debouncedUpdateDeviceInfo)
      }
    }

    const orientationHandler = () => {
      // 方向变化时延迟更新，移动端更长的延迟
      const isMobile = window.innerWidth <= 768;
      setTimeout(updateDeviceInfo, isMobile ? 400 : 200) // 移动端400ms，桌面端200ms
    }

    window.addEventListener('resize', resizeHandler, { passive: true })
    window.addEventListener('orientationchange', orientationHandler, { passive: true })

    return () => {
      window.removeEventListener('resize', resizeHandler)
      window.removeEventListener('orientationchange', orientationHandler)
    }
  }, [updateDeviceInfo, debouncedUpdateDeviceInfo])

  return deviceInfo
}

// 简化的移动端检测Hook - 优化版本
export function useIsMobile(): boolean {
  const { isMobile } = useMobile()
  return isMobile
}

// 设备类型检测Hook - 优化版本
export function useDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  const { deviceType } = useMobile()
  return deviceType
}

// 轻量级设备检测 - 用于性能关键场景
export function useDeviceTypeLight(): 'mobile' | 'tablet' | 'desktop' {
  const [deviceType, setDeviceType] = useState<'mobile' | 'tablet' | 'desktop'>('desktop')

  useEffect(() => {
    if (typeof window === 'undefined') return

    const updateDeviceType = () => {
      const newDeviceType = getDeviceType(window.innerWidth)
      setDeviceType(prev => prev === newDeviceType ? prev : newDeviceType)
    }

    updateDeviceType()
    
    const debouncedUpdate = debounce(updateDeviceType, 150)
    window.addEventListener('resize', debouncedUpdate, { passive: true })

    return () => {
      window.removeEventListener('resize', debouncedUpdate)
    }
  }, [])

  return deviceType
}
